import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { MicroLabel } from "../components/ui/Text";
import { useSession } from "../lib/session";

/**
 * The route guard.
 *
 * It gates *rendering*, not access — every endpoint behind it authenticates
 * independently, so a hand-crafted URL reaches a 401 from the server rather than
 * data. This exists so an unauthenticated visitor sees the login screen instead
 * of a page of failed requests.
 *
 * The waiting state renders nothing but a caption. Redirecting while `/auth/me`
 * is still in flight would bounce a signed-in operator to the login screen on
 * every hard refresh.
 */
export function RequireAuth({ children }: { readonly children: ReactNode }) {
  const { principal, isResolving } = useSession();
  const location = useLocation();

  if (isResolving) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-paper">
        <MicroLabel>Checking session</MicroLabel>
      </div>
    );
  }

  if (principal === undefined) {
    return (
      <Navigate
        to="/login"
        replace
        // Carried in router state, not the query string: it never reaches the
        // address bar, so it cannot be crafted from outside the app.
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <>{children}</>;
}
